import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Typography, Box, Alert, Chip,
  Paper, IconButton, Grid, Card, CardContent
} from '@mui/material';
import AdminTable from '../../components/shared/AdminTable';
import UndoIcon from '@mui/icons-material/Undo';
import ReplayIcon from '@mui/icons-material/Replay';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import billingApi from '../../services/billingApi';
import { formatDate } from '../../utils/formatters';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';
import { MANUAL_REVIEW_STATUS } from '../../utils/workflowStatus';

// 999.4741: the full set of statuses an operator can filter the grid to,
// including manual_review — before this fix there was NO way to find a
// manual_review instance from this page at all.
export const STATUS_FILTER_OPTIONS = [
  'running', 'paused_for_approval', 'paused_for_input', 'paused_for_bulk',
  'paused_for_payment', // 999.15518: same gap as paused_for_bulk (999.4759)
  MANUAL_REVIEW_STATUS, 'completed', 'failed', 'canceled', 'undone',
];

export function statusColor(status) {
  const colors = {
    completed: 'success', running: 'info', pending: 'default',
    paused_for_approval: 'warning', paused_for_input: 'warning',
    paused_for_bulk: 'warning', paused_for_payment: 'warning',
    failed: 'error', canceled: 'default', undone: 'secondary'
    // manual_review deliberately has NO entry here — it renders via its own
    // outlined+icon chip below, never the filled color scheme every other
    // status uses, so it can never be visually mistaken for 'failed' (we
    // don't know it failed) or a routine paused_* state (999.4741: there is
    // no supported transition out of it, unlike the paused_* states).
  };
  return colors[status] || 'default';
}

function stepStatusColor(status) {
  const colors = {
    completed: 'success', running: 'info', failed: 'error',
    skipped: 'default', pending: 'default',
    reverted: 'warning', revert_failed: 'error',
    // 999.4737 (harrison review, item 7): 'reverting' — a step actively
    // being compensated (claim landed, provider call not yet resolved) —
    // used to fall through to the 'default' grey chip, identical to a step
    // that never ran; that is misleading on a money-path monitor.
    reverting: 'info'
  };
  return colors[status] || 'default';
}

function formatDuration(start, end) {
  if (!start || !end) return '—';
  const ms = new Date(end) - new Date(start);
  if (ms < 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

function StatCard({ title, value, color, onClick }) {
  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={onClick ? { cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } } : undefined}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        <Typography variant="h5" sx={{ color }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

export default function WorkflowMonitor() {
  const [instances, setInstances] = useState([]);
  // 999.4741 WARN 7 (harrison review): loadData's main list is `?limit=100`
  // ordered newest-first, and a manual_review instance is >=24h old BY
  // CONSTRUCTION (999.4740's idempotency-window refusal) — under real
  // volume it can fall off that page entirely, so the filter/stat above
  // find nothing even though instances exist. Fetch the manual_review set
  // SEPARATELY, server-side filtered (`?status=`, already supported by
  // workflow.controller.js), so discoverability doesn't depend on recency.
  const [manualReviewInstances, setManualReviewInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  // 'all' = the normal recency-ordered grid; 'manual_review' = the dedicated
  // view backed by the server-filtered fetch above, not the paginated list.
  const [viewMode, setViewMode] = useState('all');

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [instData, manualReviewData] = await Promise.all([
        billingApi.get('/api/admin/workflows/instances?limit=100'),
        billingApi.get(`/api/admin/workflows/instances?status=${MANUAL_REVIEW_STATUS}&limit=100`),
      ]);
      setInstances(instData.instances || []);
      setManualReviewInstances(manualReviewData.instances || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRowClick(row) {
    const id = row.id;
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedData(null);
      return;
    }
    try {
      const data = await billingApi.get(`/api/admin/workflows/instances/${id}`);
      setExpandedId(id);
      setExpandedData(data);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleUndo(id) {
    try {
      await billingApi.post(`/api/admin/workflows/instances/${id}/undo`);
      loadData();
    } catch (err) { setError(err.message); }
  }

  async function handleRetry(id) {
    try {
      await billingApi.post(`/api/admin/workflows/instances/${id}/retry`);
      loadData();
    } catch (err) { setError(err.message); }
  }

  async function handleCancel(id) {
    try {
      await billingApi.post(`/api/admin/workflows/instances/${id}/cancel`, { reason: 'Canceled from monitor' });
      loadData();
    } catch (err) { setError(err.message); }
  }

  async function handleRollback(id) {
    if (!window.confirm('Rollback this failed workflow? This will revert all completed steps (Stripe charges, DB changes, etc.)')) return;
    try {
      const result = await billingApi.post(`/api/admin/workflows/instances/${id}/rollback`);
      const r = result.rollback || {};
      const msg = `Rolled back ${r.reverted?.length || 0} step(s)${r.failed?.length ? `, ${r.failed.length} failed` : ''}`;
      alert(msg);
      loadData();
    } catch (err) { setError(err.message); }
  }

  // Stats
  const stats = {
    total: instances.length,
    running: instances.filter(i => i.status === 'running' || i.status === 'paused_for_approval').length,
    completed: instances.filter(i => i.status === 'completed').length,
    failed: instances.filter(i => i.status === 'failed').length,
    // 999.4741: visible on load, without needing to filter — these are the
    // instances where a Stripe refund/charge may already have executed and
    // is unverified (999.4740). WARN 7: counted from the DEDICATED
    // server-filtered fetch, not the recency-limited main list, so the
    // count is accurate however old these instances are.
    manualReview: manualReviewInstances.length,
  };

  const displayedRows = viewMode === 'manual_review' ? manualReviewInstances : instances;

  const columns = useMemo(() => [
    {
      field: 'definition_name',
      headerName: 'Workflow',
      width: 200,
      renderCell: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row.definition_name || row.workflowId || 'Unknown'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace" }}>
            {row.id.substring(0, 8)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 160,
      type: 'singleSelect',
      filterOptions: STATUS_FILTER_OPTIONS,
      renderCell: (row) => (
        row.status === MANUAL_REVIEW_STATUS ? (
          <Chip
            icon={<WarningAmberIcon fontSize="small" />}
            label={row.status}
            variant="outlined"
            color="error"
            size="small"
          />
        ) : (
          <Chip label={row.status} color={statusColor(row.status)} size="small" />
        )
      ),
    },
    {
      field: 'current_step_id',
      headerName: 'Current Step',
      width: 170,
      renderCell: (row) => row.current_step_id ? row.current_step_id.replace(/_/g, ' ') : '—',
    },
    {
      field: 'updated_at',
      headerName: 'Updated',
      width: 170,
      type: 'date',
      renderCell: (row) => formatDate(row.updated_at),
    },
    {
      field: 'started_by',
      headerName: 'Started By',
      width: 140,
      renderCell: (row) => (
        <Typography variant="body2" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.75rem' }}>
          {row.started_by?.substring(0, 8)}
        </Typography>
      ),
    },
    {
      field: 'started_at',
      headerName: 'Started',
      width: 170,
      type: 'date',
      renderCell: (row) => formatDate(row.started_at),
    },
    {
      field: 'duration',
      headerName: 'Duration',
      width: 100,
      sortable: false,
      filterable: false,
      valueGetter: (params) => formatDuration(params.row.started_at, params.row.completed_at || new Date()),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (row) => {
        const inst = row;
        return (
          <Box onClick={e => e.stopPropagation()}>
            {inst.status === 'completed' && inst.undo_eligible_until && new Date(inst.undo_eligible_until) > new Date() && (
              <IconButton size="small" title="Undo" onClick={() => handleUndo(inst.id)}>
                <UndoIcon fontSize="small" />
              </IconButton>
            )}
            {inst.status === 'failed' && (
              <>
                <IconButton size="small" title="Retry" onClick={() => handleRetry(inst.id)}>
                  <ReplayIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" title="Rollback" onClick={() => handleRollback(inst.id)}
                  sx={{ color: 'warning.main' }}>
                  <UndoIcon fontSize="small" />
                </IconButton>
              </>
            )}
            {(inst.status === 'running' || inst.status === 'paused_for_approval' || inst.status === 'paused_for_input' || inst.status === 'paused_for_bulk' || inst.status === 'paused_for_payment') && (
              <IconButton size="small" title="Cancel" onClick={() => handleCancel(inst.id)}>
                <CancelIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      },
    },
  ], []);

  const stepColumns = [
    {
      field: 'step_id',
      headerName: 'Step',
      width: 180,
      // 999.15608: data-testid so e2e can distinguish the Step cell from the
      // Type cell — when step_id and step_type share the same value (e.g.
      // 'cancel_subscription'), getByRole('cell', {name: ...}) matches both.
      renderCell: (row) => (
        <span data-testid="workflow-step-name">{row.step_id?.replace(/_/g, ' ') || ''}</span>
      ),
    },
    {
      field: 'step_type',
      headerName: 'Type',
      width: 140,
      renderCell: (row) => <Chip label={row.step_type?.replace(/_/g, ' ')} size="small" variant="outlined" />,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (row) => <Chip label={row.status} color={stepStatusColor(row.status)} size="small" />,
    },
    {
      field: 'duration',
      headerName: 'Duration',
      width: 100,
      valueGetter: (params) => formatDuration(params.row.started_at, params.row.completed_at),
    },
    {
      field: 'error_message',
      headerName: 'Error',
      flex: 1,
      renderCell: (row) => row.error_message ? (
        <Typography variant="caption" color="error">{row.error_message}</Typography>
      ) : null,
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Workflow Monitor</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats — 5 cards at xs=2.4 (12/5) so they stay on one row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={2.4}><StatCard title="Total" value={stats.total} onClick={() => setViewMode('all')} /></Grid>
        <Grid item xs={2.4}><StatCard title="Active" value={stats.running} color="info.main" onClick={() => setViewMode('all')} /></Grid>
        <Grid item xs={2.4}><StatCard title="Completed" value={stats.completed} color="success.main" onClick={() => setViewMode('all')} /></Grid>
        <Grid item xs={2.4}><StatCard title="Failed" value={stats.failed} color="error.main" onClick={() => setViewMode('all')} /></Grid>
        <Grid item xs={2.4}>
          <StatCard
            title="Needs Review"
            value={stats.manualReview}
            color="error.main"
            onClick={() => setViewMode('manual_review')}
          />
        </Grid>
      </Grid>

      {viewMode === 'manual_review' && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setViewMode('all')}>
          Showing only manual_review instances ({manualReviewInstances.length}), fetched directly
          by status — these can be far older than the 100 most recent instances shown in the main
          view.
        </Alert>
      )}

      {loading && instances.length === 0 ? (
        <AdminPageSkeleton bare rows={5} />
      ) : (
        <>
          <Paper variant="outlined" sx={{ height: expandedId ? 'calc(100vh - 550px)' : 'calc(100vh - 300px)', minHeight: 300 }}>
            <AdminTable
              rows={displayedRows}
              columns={columns}
              defaultSort={[{ field: 'started_at', direction: 'desc' }]}
              onRowClick={handleRowClick}
            />
          </Paper>

          {/* Detail panel below grid */}
          {expandedId && expandedData && expandedData.instance?.id === expandedId && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Step Log: {expandedData.instance.definition_name || expandedData.instance.workflowId}
                </Typography>
                <IconButton size="small" onClick={() => { setExpandedId(null); setExpandedData(null); }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* 999.4741: manual_review has NO supported transition out of
                  it (retryStep/cancelWorkflow/rollbackInstance all refuse
                  it) — this is currently the only place an operator can see
                  what happened. Show exactly what they need to investigate
                  at the provider: current step, when it stopped, and why. */}
              {expandedData.instance?.status === MANUAL_REVIEW_STATUS && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Parked for manual review — no automatic retry will occur
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                    Current step: {expandedData.instance.current_step_id || '—'} · Last updated: {formatDate(expandedData.instance.updated_at)}
                  </Typography>
                  {expandedData.instance.error_message && (
                    <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                      Reason: {expandedData.instance.error_message}
                    </Typography>
                  )}
                </Alert>
              )}

              <Box sx={{ height: 250 }}>
                <AdminTable
                  rows={expandedData.step_logs || []}
                  columns={stepColumns}
                  defaultSort={[]}
                />
              </Box>
              {expandedData.instance?.status !== MANUAL_REVIEW_STATUS && expandedData.instance?.error_message && (
                <Alert severity="error" sx={{ mt: 1 }}>{expandedData.instance.error_message}</Alert>
              )}
            </Paper>
          )}
        </>
      )}
    </Container>
  );
}
